import { cloneDeep } from 'lodash';
import {
  ChangeEvent,
  FC,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { Close, Fullscreen, Refresh } from '@gmm/icons';
import {
  buildProblemCanvasOwner,
  NARROW_PROBLEM_WIDTH,
  PROBLEM_WIDTH,
  StaticProblemCanvas,
} from '@gmm/problem';
import { ProblemAndAnswer } from '@gmm/sdk-voodoo/shared';
import {
  AppBar,
  Box,
  CircularProgress, // Added for loading icon
  IconButton,
  Paper,
  Tab,
  tabClasses,
  Tabs,
  Toolbar,
  Typography,
} from '@gmm/ui';
import { ids as allIds } from '~/lib/constants';
import { useFullscreenContext, Sibling } from '~/lib/fullscreenPreview';

import { useEnvConfig } from '../../environmentConfig';
import { commonEvents } from '../analytics/events';
import { useCurrentTeacher } from '../hooks/useCurrentTeacher';

import { AddSkill, ProblemPreviewTabPanel, Students } from './components';
import { MemoizedProblemCanvas } from './components/memoizedProblemCanvas';
import { SampleTypeTriangle } from './components/sampleTypeTriangle';
import { LockedExamProblemParams } from './hooks/useLockedExamTemplateProblem';
import { PreviewType } from './types';

const WIDE_MODE_MIN_W = PROBLEM_WIDTH * 1.025; // give extra 2.5% for scrollbars, margin, etc.

const ids = allIds.problemPreview;

enum SkillPreviewTab {
  Preview = 'preview',
  Answer = 'answer',
  Students = 'students',
}

interface MissedStudent {
  firstName: string;
  lastName: string;
}

export interface ProblemFromSWR {
  mutate?: () => void;
  problemData?: ProblemAndAnswer;
  refreshExamTemplateProblem?: () => Promise<void>;
  error?: any;
  isValidating?: boolean;
}

export interface PreviewProps extends ProblemFromSWR {
  description?: string;
  elevation?: number;
  isAddButtonDisabled?: boolean;
  isPreviewModal?: boolean;
  missedStudents?: MissedStudent[];
  onAdd?: () => void;
  onClose?: () => void;
  onHeightChange?: () => void;
  previewType?: PreviewType;
  showRefresh?: boolean;
  siblingSkills?: Sibling[];
  studentInClassId?: string;
  title?: ReactNode;
  type?: string;
  studentInTestId?: string;
  examRestoreId?: string;
  problemTabText?: string;
  helpRequestedProblemData?: ProblemAndAnswer;
  examTemplate?: boolean;
  lockedExamProblemParams?: LockedExamProblemParams;
  isSkillDiscoveryLockedExamProblem?: boolean;
}

export const ProblemPreview: FC<PreviewProps> = ({
  mutate: refresh,
  problemData = null,
  refreshExamTemplateProblem,
  error,
  isValidating,
  description,
  elevation,
  isAddButtonDisabled,
  isPreviewModal = false,
  missedStudents,
  onAdd,
  onClose,
  onHeightChange,
  previewType = PreviewType.Sample,
  showRefresh,
  siblingSkills,
  studentInClassId,
  title,
  type = 'skill preview',
  studentInTestId,
  examRestoreId,
  problemTabText,
  helpRequestedProblemData,
  examTemplate,
  lockedExamProblemParams,
  isSkillDiscoveryLockedExamProblem = false,
}) => {
  const [t] = useTranslation(['problemPreview']);
  const { openFullscreenWith } = useFullscreenContext();
  const { data: teacher } = useCurrentTeacher();
  const config = useEnvConfig();

  const [tab, setTab] = useState<SkillPreviewTab>(SkillPreviewTab.Preview);

  // tab defaults to preview, but then each ProblemPreview should know its own tab choice
  const handleTabChange = (
    event: ChangeEvent<unknown>,
    newTab: SkillPreviewTab,
  ): void => {
    event.stopPropagation();
    commonEvents.previewSkill(tab, newTab);
    setTab(newTab);
  };

  // State to store the last known dimensions of the MemoizedProblemCanvas
  const [lastCanvasDimensions, setLastCanvasDimensions] = useState<{
    height: number | null;
    width: number | null;
  }>({ height: null, width: null });

  // Ref to the MemoizedProblemCanvas container
  const canvasRef = useRef<HTMLDivElement>(null);

  // Measure the dimensions of the MemoizedProblemCanvas when it renders
  useEffect(() => {
    if (canvasRef.current) {
      const { height, width } = canvasRef.current.getBoundingClientRect();

      setLastCanvasDimensions({ height, width });
    }
  }, [problemData]); // Re-measure when problemData changes

  const problemCanvasOwner = useMemo(() => {
    if (!config) return null;

    return buildProblemCanvasOwner({
      getUsername: () => teacher?.email,
      config,
    });
  }, [config, teacher]);

  const handleRefresh = async (): Promise<void> => {
    commonEvents.getNewSkillSample(type);
    if (refreshExamTemplateProblem) await refreshExamTemplateProblem();
    else refresh?.();
  };

  const handleFullscreenClick = (): void => {
    if (!problemData) return;

    const args = {
      type: `${problemData.subcat}`,
      title,
      description,
      siblingSkills: siblingSkills ?? [],
      studentInClassId,
      studentProblemData: studentInClassId ? problemData : null,
      studentInTestId,
      examRestoreId,
      helpRequestedProblemData,
      lockedExamProblemParams,
      isSkillDiscoveryLockedExamProblem,
    };

    openFullscreenWith(args);
  };

  useEffect(() => {
    onHeightChange?.();
  }, [onHeightChange]);

  const [isWideScreen, setIsWideScreen] = useState(
    window.innerWidth > WIDE_MODE_MIN_W,
  );

  useEffect(() => {
    const handleResize = (): void => {
      setIsWideScreen(window.innerWidth > WIDE_MODE_MIN_W);
    };

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Always send a clone since ProblemCanvas mutates
  const problemDataClone = useMemo(() => cloneDeep(problemData), [problemData]);

  const iAmLockedExam = !!lockedExamProblemParams;
  const iAmRandomExam = !!(examTemplate && !lockedExamProblemParams);

  if (!config || !problemCanvasOwner) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: lastCanvasDimensions.height || 300,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      data-intercom-target="skill-preview-area"
      data-testid={ids.main}
      sx={{ position: 'relative', width: 1 }}
    >
      <AppBar color="default" elevation={elevation} position="static">
        <Toolbar variant="dense">
          <Tabs
            data-testid={ids.tabs.main}
            onChange={handleTabChange}
            sx={{ [`.${tabClasses.root}`]: { minWidth: 160 } }}
            value={tab}
          >
            <Tab
              data-intercom-target="skill-preview-tabs-problem"
              data-testid={ids.tabs.problem}
              label={
                problemTabText || t(`problemPreview:problemTab.${previewType}`)
              }
              value={SkillPreviewTab.Preview}
            />
            <Tab
              data-intercom-target="skill-preview-tabs-answer"
              data-testid={ids.tabs.answer}
              label={t('problemPreview:answerTab')}
              value={SkillPreviewTab.Answer}
            />
            {missedStudents?.length && (
              <Tab
                data-intercom-target="skill-preview-tabs-missed-students"
                data-testid={ids.tabs.students}
                label={t('problemPreview:studentsTab')}
                value={SkillPreviewTab.Students}
              />
            )}
          </Tabs>
          {!!onAdd && (
            <AddSkill
              disabled={isAddButtonDisabled || error}
              isLoading={!!isValidating}
              onAdd={onAdd}
              iAmLockedExam={iAmLockedExam}
              iAmRandomExam={iAmRandomExam}
              problemLoadingError={error}
            />
          )}
          <Box sx={{ flex: '1 1 auto' }} />
          {!isPreviewModal && (
            <IconButton onClick={handleFullscreenClick}>
              <Fullscreen />
            </IconButton>
          )}
          {showRefresh && (
            <IconButton
              aria-label={t('problemPreview:buttons.refresh')}
              data-intercom-target="get-new-sample-problem"
              data-testid={ids.refresh}
              disabled={isValidating}
              onClick={handleRefresh}
            >
              <Refresh color="secondary" />
            </IconButton>
          )}
          {!!onClose && (
            <IconButton
              aria-label={t('problemPreview:buttons.close')}
              data-intercom-target="skill-preview-close"
              data-testid={ids.close}
              onClick={onClose}
            >
              <Close />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>
      <Paper
        elevation={elevation}
        square
        sx={{
          height: 1,
          p: 1,
          textAlign: 'center',
          position: 'relative',
        }}
      >
        <ProblemPreviewTabPanel name={SkillPreviewTab.Preview} value={tab}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center', // Center the loading icon or error message vertically
              minHeight:
                !error && !problemData && lastCanvasDimensions.height
                  ? lastCanvasDimensions.height
                  : undefined,
            }}
          >
            {!error && problemDataClone && problemDataClone.problem ? (
              <div ref={canvasRef}>
                <MemoizedProblemCanvas
                  problemData={{ r: problemDataClone.problem.r }}
                  problemCanvasOwner={problemCanvasOwner}
                  userId={problemDataClone?.id}
                  globalFocus={false}
                  problemWidth={
                    isPreviewModal && isWideScreen
                      ? PROBLEM_WIDTH
                      : NARROW_PROBLEM_WIDTH
                  }
                />
              </div>
            ) : !problemData && !error ? (
              <CircularProgress />
            ) : error ? (
              <Typography align="center" variant="h4">
                {t('problemPreview:fetchingProblem.error')}
              </Typography>
            ) : null}
          </Box>
          {!error && iAmLockedExam && <SampleTypeTriangle exact={true} />}
          {!error && iAmRandomExam && <SampleTypeTriangle exact={false} />}
        </ProblemPreviewTabPanel>
        <ProblemPreviewTabPanel name={SkillPreviewTab.Answer} value={tab}>
          {!error && problemDataClone && problemDataClone.answer ? (
            <StaticProblemCanvas
              config={config}
              problemData={{ r: problemDataClone.answer.r }}
            />
          ) : !problemData && !error ? (
            <CircularProgress />
          ) : error ? (
            <Typography align="center" variant="h4">
              {t('problemPreview:fetchingProblem.errorAnswer')}
            </Typography>
          ) : null}
        </ProblemPreviewTabPanel>
        {missedStudents?.length && (
          <ProblemPreviewTabPanel name={SkillPreviewTab.Students} value={tab}>
            <Students students={missedStudents} />
          </ProblemPreviewTabPanel>
        )}
      </Paper>
    </Box>
  );
};

export default ProblemPreview;
